/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import Select from 'react-select';
import './LikenessMaster.css';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import Loader from '../../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import * as action from '../Store/SystemMasters.action';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';

const EMPTY_LIKENESS = { name: null, isEmpty: false, isNew: true, isActive: true, _id: '', incoterms: [] };
function CustomAttrSave(isLoading) {
  return isLoading ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />;
}

function LikenessMaster() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const {
    SystemMastersDetails: { licensorList, licenseList, licenseDropdownList, likenessList, activeIncotermsList },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = useSelector((state) => state);
  const { CheckActionPermission } = useComponent();
  const MasterAddEditAccess = CheckActionPermission(logInRights, 'masters', 'likeness', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'masters', 'likeness', 'delete');
  const loggedInUserId = userInfo.userId;

  const [licensorSelectData, setLicensorSelectData] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [likenessData, setLikenessData] = useState([]);
  const [addingLikeness, setAddingLikeness] = useState(false);
  const [buttonDown, setButtonDown] = useState(true);
  const [showSection, setShowSection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [statusChangeId, setStatusChangeId] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [isLoadingConf, setIsLoadingConf] = useState(false);
  const [nonUniqueIds, setNonUniqueIds] = useState([]);
  const [editedLikenessId, setEditedLikenessId] = useState(-1);

  const btnDisabled = useMemo(() => likenessData?.some((item) => !item.name?.trim()), [likenessData]);
  const isUnique = nonUniqueIds.length === 0;

  useEffect(() => {
    setLikenessData(addingLikeness ? [...likenessList, EMPTY_LIKENESS] : likenessList);
  }, [likenessList, addingLikeness]);

  useEffect(() => {
    if (selectedLicense?._id) likenessCalled();
  }, [selectedLicense]);

  useEffect(() => {
    const duplicates = findDuplicateIndices(likenessData);
    setNonUniqueIds(duplicates);
  }, [likenessData]);

  useEffect(() => {
    // If the selected license is deleted, clear selection and refetch for licenses
    if (selectedLicense && licenseList?.length > 0 && !licenseList?.some((lic) => lic._id === selectedLicense._id && lic.isActive)) {
      if (licensorSelectData?._id) {
        fetchLicenses(licensorSelectData._id);
      }
      return; // Prevents double fetch if both conditions match
    }

    // If the license list changes (add case), refetch for licenses
    if (licensorSelectData?._id) {
      fetchLicenses(licensorSelectData._id);
    }
  }, [licensorList, licenseList, selectedLicense, licensorSelectData]);

  useEffect(() => {
    dispatch(action.getActiveIncotermsDetails({ isActive: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findDuplicateIndices = useCallback((list) => {
    const map = {};
    list.forEach((item, idx) => {
      const name = item?.name?.trim();
      if (!name) return;
      map[name] = map[name] || [];
      map[name].push(idx);
    });
    return Object.values(map).flatMap((arr) => (arr.length > 1 ? arr : []));
  }, []);

  const handleChanged = (e, i) => {
    const val = e.target.value;
    const updated = [...likenessData];
    updated[i] = { ...updated[i], name: val, isEmpty: !val.trim() };
    setLikenessData(updated);
  };

  const handleAdd = async () => {
    if (!showSection) {
      setAddingLikeness(true);
      toggleSection();
      if (isEmpty(licenseDropdownList)) await dispatch(action.getLicensorDetails({ loggedInUserId }));
    } else {
      setLikenessData((prev) => [...prev, EMPTY_LIKENESS]);
    }
  };

  const toggleSection = async () => {
    setButtonDown(!buttonDown);
    setShowSection(buttonDown);
    setIsLoading(true);
    if (buttonDown && (!licensorList || licensorList.length === 0)) await dispatch(action.getLicensorDetails({ loggedInUserId }));
    setIsLoading(false);
  };

  const handleDelete = (id) => {
    if (typeof id === 'number' || id === '') {
      const updated = [...likenessData];
      updated.splice(id, 1);
      setLikenessData(updated);
    } else {
      setDeleteId(id);
      setShowDeleteConfirm(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const payload = likenessData.map((item) => ({
      ...item,
      name: item.name?.trim(),
      isEmpty: !item.name?.trim(),
    }));
    if (payload.every((item) => item.name)) {
      const params = {
        licensorId: licensorSelectData?._id,
        licenseId: selectedLicense?._id,
        names: JSON.stringify(payload),
        title: 'likeness',
      };
      await dispatch(action.saveToSystemMaster(params, t));
      setAddingLikeness(false);
      await likenessCalled();
    } else {
      setLikenessData(payload);
    }
    setIsSaving(false);
  };

  const fetchLicenses = async (licensorId) => {
    const params = {
      licensorId,
      title: 'license',
      isActive: true,
    };
    await dispatch(action.getLicenseDropdownList(params));
  };

  const handleLicensorChange = async (selected) => {
    setLicensorSelectData(selected);
    setSelectedLicense(null);
    if (selected?._id) await fetchLicenses(selected._id);
  };

  const likenessCalled = async () => {
    await dispatch(action.getLikenessDetails({ licensorId: licensorSelectData._id, licenseId: selectedLicense._id, loggedInUserId }));
  };

  const handleStatusToggle = (id) => {
    setStatusChangeId(id);
    setShowStatusConfirm(true);
  };

  const confirmDelete = async () => {
    setIsLoadingConf(true);
    await dispatch(action.deleteFromSystemMasters({ loggedInUserId, likenessId: deleteId, title: 'likeness' }, t));
    setIsLoadingConf(false);
    setShowDeleteConfirm(false);
    await likenessCalled();
  };

  const confirmStatusChange = async () => {
    setIsLoadingConf(true);
    const item = likenessData.find((ele) => ele._id === statusChangeId);
    if (item) {
      const payload = {
        id: item._id,
        licensorId: item.licensorId,
        licenseId: item.licenseId,
        isActive: !item.isActive,
        loggedInUserId,
        title: 'likeness',
      };
      await dispatch(action.setActiveInactive(payload, t));
    }
    setIsLoadingConf(false);
    setShowStatusConfirm(false);
    await likenessCalled();
  };

  const activeLicensors = useMemo(() => licensorList.filter((l) => l.isActive), [licensorList]);
  const activeLicenses = useMemo(
    () => licenseDropdownList.filter((l) => l.licensorId === licensorSelectData?._id && l.isActive),
    [licenseDropdownList, licensorSelectData],
  );

  const handleLikenessEdit = (id) => {
    setEditedLikenessId(id);
  };

  const handleAddIncoterms = (id) => {
    const selectedIndex = likenessData?.findIndex((item) => item._id === id);
    const updated = [...likenessData];

    const newIncoterm = {
      incotermsId: '', // Placeholder until user selects an actual incoterms ID
      value: '', // Placeholder, should be a number ≤ 100
      isActive: true, // Default true as per schema
    };

    updated[selectedIndex] = {
      ...updated[selectedIndex],
      incoterms: updated[selectedIndex]?.incoterms ? [...updated[selectedIndex].incoterms, newIncoterm] : [newIncoterm],
    };

    setLikenessData(updated);
  };

  const setLikenessIncoterms = (value, id, index, key) => {
    const updated = [...likenessData];
    const licenseIndex = likenessData?.findIndex((item) => item._id === id);
    updated[licenseIndex] = {
      ...updated[licenseIndex],
      incoterms: updated[licenseIndex]?.incoterms ? updated[licenseIndex].incoterms.map((item, ind) => (ind === index ? { ...item, [key]: value } : item)) : [],
    };
    setLikenessData(updated);
  };

  const handleSaveSingleLikeness = async () => {
    setIsSaving(true);
    const payload = likenessData.map((item) => ({
      ...item,
      name: item.name?.trim(),
      isEmpty: !item.name?.trim(),
    }));
    if (payload.every((item) => item.name)) {
      const params = {
        licensorId: licensorSelectData?._id,
        licenseId: selectedLicense?._id,
        names: JSON.stringify(payload),
        title: 'likeness',
      };
      await dispatch(action.saveToSystemMaster(params, t));
      setAddingLikeness(false);
      await likenessCalled();
    } else {
      setLikenessData(payload);
    }
    setIsSaving(false);
  };

  const handleIncotermsStatusToggle = (id, index, key) => {
    const updated = [...likenessData];
    const licenseIndex = likenessData?.findIndex((item) => item._id === id);
    updated[licenseIndex] = {
      ...updated[licenseIndex],
      incoterms: updated[licenseIndex]?.incoterms
        ? updated[licenseIndex].incoterms.map((item, ind) => (ind === index ? { ...item, [key]: !item[key] } : item))
        : [],
    };
    setLikenessData(updated);
  };

  const handleIncotermsDelete = (id, index) => {
    const updated = [...likenessData];
    const licenseIndex = likenessData?.findIndex((item) => item._id === id);
    updated[licenseIndex] = {
      ...updated[licenseIndex],
      incoterms: updated[licenseIndex]?.incoterms ? updated[licenseIndex].incoterms.filter((item, ind) => ind !== index) : [],
    };
    setLikenessData(updated);
  };
  return (
    <div>
      <Accordion className="mb-4 master-accordian style-two custom-attribute-master">
        <Card className="card">
          <Card.Header>
            <div className="title-container">
              <p>{t('LIKENESS_MASTER.TITLE')}</p>
            </div>
            <div className="action-title-btns">
              <CustomTooltip tooltipText={buttonDown ? t('CUSTOM_ATTRIBUTES.OPEN') : t('CUSTOM_ATTRIBUTES.CLOSE')}>
                <button type="button" onClick={toggleSection} className="btn d-inline-flex p-0" aria-label="Toggle Section">
                  <i className={`icon-new-down-arrow-icon f-24${!buttonDown ? ' icon-inverse' : ''}`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showSection && (
            <Card.Body>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  <div className="select-container">
                    <div className="common-react-select">
                      <Select
                        options={activeLicensors}
                        placeholder={t('LICENSOR_MASTER.SELECT_LICENSOR')}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o._id}
                        value={licensorSelectData}
                        onChange={handleLicensorChange}
                        className="common-select"
                        classNamePrefix="common-select"
                        maxMenuHeight={130}
                      />
                    </div>
                    <div className="common-react-select mb-0">
                      <Select
                        options={activeLicenses}
                        placeholder={t('LICENSE_MASTER.SELECT_LICENSE')}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o._id}
                        value={selectedLicense}
                        onChange={setSelectedLicense}
                        className="common-select"
                        classNamePrefix="common-select"
                        maxMenuHeight={130}
                        isDisabled={!licensorSelectData}
                      />
                    </div>
                  </div>
                  <div className="common-scrollbar flex-fill">
                    <ul>
                      {selectedLicense &&
                        likenessData.map((item, i) => (
                          <li key={i}>
                            <div className={`edit-mode-container ${editedLikenessId === item._id ? 'open' : ''}`}>
                              <div className="list-view" style={editedLikenessId === item._id ? { display: 'none' } : { display: 'flex' }}>
                                <div className="input-container">
                                  <input
                                    placeholder={t('LIKENESS_MASTER.LIKENESS_PLACEHOLDER')}
                                    type="text"
                                    maxLength={30}
                                    value={item?.name ?? ''}
                                    onChange={(e) => handleChanged(e, i)}
                                  />
                                  {nonUniqueIds.includes(i) && <label className="text-danger p-1">{t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}</label>}
                                  {item.isEmpty && (
                                    <label className="text-danger p-1">
                                      {t('LIKENESS_MASTER.TITLE')} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
                                    </label>
                                  )}
                                </div>
                                <div className="input-action d-flex">
                                  {item._id && MasterAddEditAccess && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn p-0 custom-action mr-0"
                                        onClick={() => handleLikenessEdit(item._id)}
                                        aria-label="Edit"
                                      >
                                        <span className="icon-Edit-1 f-20" />
                                      </button>
                                      <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.ACTIVE/INACTIVE')}>
                                        <button
                                          type="button"
                                          className="btn p-0 custom-action mr-0"
                                          onClick={() => handleStatusToggle(item._id)}
                                          id={item?._id}
                                          aria-label={item?.isActive ? 'Active' : 'Inactive'}
                                        >
                                          <span className={item.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                                        </button>
                                      </CustomTooltip>
                                    </>
                                  )}
                                  {MasterDeleteAccess && (
                                    <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.DELETE')}>
                                      <button type="button" className=" btn custom-action mr-0" onClick={() => handleDelete(item._id || i)} aria-label="delete">
                                        <span className="icon-Delete f-24" />
                                      </button>
                                    </CustomTooltip>
                                  )}
                                </div>
                              </div>
                              <div className="edit-mode" style={editedLikenessId === item._id ? { display: 'block' } : { display: 'none' }}>
                                <div className="d-flex align-items-center">
                                  <div className="input-container">
                                    <input
                                      placeholder={t('LIKENESS_MASTER.LIKENESS_PLACEHOLDER')}
                                      type="text"
                                      name="likeness"
                                      maxLength={30}
                                      value={item?.name ?? ''}
                                      autoFocus={!item?._id}
                                      onChange={(e) => handleChanged(e, i)}
                                      disabled
                                    />
                                  </div>
                                  <div className="input-action d-flex">
                                    <button aria-label="Add" type="button" onClick={() => handleAddIncoterms(item._id)} className="btn p-0 custom-action mr-0">
                                      <span className="icon-Add f-24" />
                                    </button>
                                    <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                                      <button aria-label="Save" type="button" onClick={() => handleSaveSingleLikeness()} className="btn p-0 custom-action mr-0">
                                        <span className="icon-Save-1 f-24" />
                                      </button>
                                    </CustomTooltip>
                                    <button aria-label="Close" type="button" onClick={() => setEditedLikenessId(-1)} className="btn p-0 custom-action mr-0">
                                      <span className="icon-close cursor-pointer f-20" />
                                    </button>
                                  </div>
                                </div>
                                <p className="mb-0 mt-2 text-bold">Incoterms</p>
                                {item?.incoterms?.length > 0 ? (
                                  item?.incoterms.map((incoterm, indIncoterms) => (
                                    <div key={incoterm?._id} className="item-row">
                                      <div className="common-react-select flex-fill mb-0">
                                        <Select
                                          options={activeIncotermsList}
                                          placeholder={t('LICENSE_MASTER.SELECT_INCOTERMS')}
                                          getOptionLabel={(option) => option.name}
                                          getOptionValue={(option) => option._id}
                                          name="incoterms"
                                          menuPosition="fixed"
                                          value={incoterm?.incotermsId}
                                          maxMenuHeight={130}
                                          className="common-select"
                                          classNamePrefix="common-select"
                                          onChange={(value) => setLikenessIncoterms(value, item._id, indIncoterms, 'incotermsId')}
                                        />
                                      </div>
                                      <div className="input-per-container">
                                        <input
                                          type="text"
                                          value={incoterm?.value}
                                          onChange={(e) => setLikenessIncoterms(e.target.value, item._id, indIncoterms, 'value')}
                                          className="form-control text-percentage f-16 ml-16 mr-8"
                                          onBlur={(e) => {
                                            const value = parseFloat(e.target.value);
                                            if (value > 100) {
                                              e.target.value = 100;
                                              setLikenessIncoterms(100, item._id, indIncoterms, 'value');
                                              toasterMsg(t('LICENSE_MASTER.VALUE_CANNOT_BE_GREATER_THAN_100'), 'danger');
                                            }
                                          }}
                                        />
                                        <span className="percentage-icon">%</span>
                                      </div>
                                      <div className="input-action d-flex">
                                        <button
                                          type="button"
                                          aria-label="Status"
                                          onClick={() => handleIncotermsStatusToggle(item._id, indIncoterms, 'isActive')}
                                          className="btn p-0 custom-action mr-0"
                                        >
                                          <span className={`icon-${incoterm?.isActive ? 'Eye-Open' : 'Eye-Closed'}`} />
                                        </button>
                                        <button
                                          type="button"
                                          aria-label="Delete"
                                          onClick={() => handleIncotermsDelete(item._id, indIncoterms)}
                                          className="btn p-0 custom-action mr-0"
                                        >
                                          <span className="icon-Delete f-24" />
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-center mb-0">{t('LICENSE_MASTER.NO_INCOTERMS')}</p>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                  <div className="license-btn-container">
                    {MasterAddEditAccess && (
                      <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                        <button
                          type="button"
                          onClick={handleAdd}
                          className="btn btn-outline sm ml-auto mr-0 mt-16 p-0"
                          disabled={btnDisabled || !isUnique}
                          aria-label="Add"
                        >
                          <span className="icon-Add text-grey" />
                        </button>
                      </CustomTooltip>
                    )}
                    {MasterAddEditAccess && (
                      <div className="section-custom-save">
                        <button
                          aria-label="Save"
                          type="button"
                          className="btn p-0"
                          onClick={handleSave}
                          disabled={!likenessData.length || btnDisabled || !isUnique || nonUniqueIds.length}
                        >
                          {CustomAttrSave(isSaving)}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>
      <ConfirmationPopup
        onOpen={showDeleteConfirm}
        onConfirm={confirmDelete}
        title={t('LIKENESS_MASTER.DELETE_CONFIRMATION.TITLE')}
        message={t('LIKENESS_MASTER.DELETE_CONFIRMATION.MESSAGE')}
        toggle={() => setShowDeleteConfirm(false)}
        loading={isLoadingConf}
      />
      <ConfirmationPopup
        onOpen={showStatusConfirm}
        onConfirm={confirmStatusChange}
        title={t('LIKENESS_MASTER.ACTIVE_INACTIVE_CONFIRMATION.TITLE')}
        message={t('LIKENESS_MASTER.ACTIVE_INACTIVE_CONFIRMATION.MESSAGE')}
        toggle={() => setShowStatusConfirm(false)}
        loading={isLoadingConf}
      />
    </div>
  );
}

export default LikenessMaster;
